import { BoundSelectField, BoundSelectFieldProps } from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { useEffect } from "react";
import { ChangeEventType, LotType } from "src/generated/graphqlTypes";
import { changeEventTypeToNameMapper } from "src/utils";
import { ChangeEventTypeBoundSelectField_ProjectFragment } from "./ChangeEventTypeBoundSelectField.generated";

type ChangeEventTypeBoundSelectFieldProps = Pick<
  BoundSelectFieldProps<ChangeEventType, string>,
  "disabled" | "disabledOptions"
> & {
  field: FieldState<any>;
  project: ChangeEventTypeBoundSelectField_ProjectFragment;
};

export function ChangeEventTypeBoundSelectField({
  project,
  field,
  disabled,
  disabledOptions,
}: ChangeEventTypeBoundSelectFieldProps) {
  // Set the default value to the lot type's default change event type upon mount
  useEffect(() => {
    if (!field.value) {
      const defaultValue = defaultChangeEventTypeByLotType(project, disabledOptions);
      if (defaultValue) field.set(defaultValue);
    }
  }, []);
  return (
    <BoundSelectField
      label="Change Event Type"
      field={field}
      options={Object.entries(ChangeEventType)}
      getOptionValue={([_, type]) => type}
      getOptionLabel={([_, type]) => `${project.lotType.clientNoun} ${changeEventTypeToNameMapper(type)}`}
      disabledOptions={[
        ...(project.shouldAccrueAsComplete
          ? [
              {
                value: ChangeEventType.External,
                reason: "This project has reached investor closeout. Changes cannot be made to the PropCo price.",
              },
            ]
          : []),
        ...(disabledOptions ?? []),
      ]}
      disabled={disabled}
    />
  );
}

function defaultChangeEventTypeByLotType(
  project: ChangeEventTypeBoundSelectField_ProjectFragment,
  disabledOptions: ChangeEventTypeBoundSelectFieldProps["disabledOptions"],
) {
  const { lotType, shouldAccrueAsComplete } = project;
  const changeEventTypeByLotType: Record<LotType, ChangeEventType> = {
    [LotType.Bool]: ChangeEventType.External,
    [LotType.Hbl]: ChangeEventType.Internal,
    [LotType.Boyl]: ChangeEventType.Internal,
    [LotType.Btr]: ChangeEventType.Internal,
    [LotType.Fee]: ChangeEventType.Internal,
  };
  // Certain projects have an agreement where every cost after completion should be HTI's cost
  // therefore we shouldn't create chargeable change events post-completion
  const defaultValue = shouldAccrueAsComplete ? ChangeEventType.Internal : changeEventTypeByLotType[lotType.code];
  // Return undefined if the default value is disabled
  const disabledValues = disabledOptions?.map((o) => (typeof o === "object" ? o.value : o)).compact() ?? [];
  return disabledValues.includes(defaultValue) ? undefined : defaultValue;
}
